import React, {useState} from 'react';
import {Building, Heart, Star, MapPin, Bed, Bath, Square, Loader} from 'lucide-react';
import {useProperty} from '../../App';

const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

const formatPrice = (price) => {
    if (!price) return 'Price on Request';
    const priceStr = price.toString();
    if (priceStr.includes('KES') || priceStr.includes('$')) return priceStr;
    return `KES ${priceStr}`;
};

const getAvailabilityBadge = (status) => {
    const normalizedStatus = status?.toLowerCase() || '';

    if (normalizedStatus.includes('available now') || normalizedStatus === 'available') {
        return {text: 'Available Now', className: 'bg-highland-500'};
    } else if (normalizedStatus.includes('soon')) {
        return {text: 'Coming Soon', className: 'bg-dawn-500'};
    } else if (normalizedStatus.includes('rented') || normalizedStatus.includes('occupied')) {
        return {text: 'Rented', className: 'bg-murram-500'};
    }
    return {text: status || 'Contact for Availability', className: 'bg-jacaranda-500'};
};

const PropertyThumbnail = () => (
    <div className="w-full h-56 bg-soil-100 flex items-center justify-center">
        <div className="text-center text-soil-400">
            <Building className="w-12 h-12 mx-auto mb-2"/>
            <p className="text-sm font-medium">Photo coming soon</p>
        </div>
    </div>
);

const PropertyCard = ({property, onClick}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const {savedProperties, toggleSaveProperty} = useProperty();

    const propertyImage = property.image || property.images?.[0] || property.thumbnail || null;
    const availabilityBadge = getAvailabilityBadge(property.available);
    const isSaved = savedProperties.includes(property.id);

    return (
        <div
            onClick={onClick}
            className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border border-soil-100"
        >
            {/* Image Container */}
            <div className="relative h-56 overflow-hidden bg-soil-100 rounded-xl mx-3 mt-3">
                {propertyImage && !imageError ? (
                    <>
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-soil-100">
                                <Loader className="w-7 h-7 text-highland-500 animate-spin"/>
                            </div>
                        )}
                        <img
                            src={propertyImage}
                            alt={property.title || 'Property'}
                            className={`w-full h-56 object-cover group-hover:scale-105 transition-transform duration-300 ${
                                imageLoaded ? 'opacity-100' : 'opacity-0'
                            }`}
                            onLoad={() => setImageLoaded(true)}
                            onError={() => {
                                setImageError(true);
                                setImageLoaded(false);
                            }}
                        />
                    </>
                ) : (
                    <PropertyThumbnail/>
                )}

                {/* Availability Badge */}
                <div className="absolute top-3 left-3">
                    <span
                        className={`${availabilityBadge.className} text-white px-3 py-1 rounded-full text-xs font-semibold shadow-md`}>
                        {availabilityBadge.text}
                    </span>
                </div>

                {/* Heart/Favorite Button */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleSaveProperty(property.id);
                    }}
                    className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all duration-200 shadow-md group/heart"
                    aria-label={isSaved ? 'Remove from saved properties' : 'Save property'}
                >
                    <Heart
                        className={`w-4 h-4 transition-colors duration-200 ${
                            isSaved ? 'text-murram-500 fill-current' : 'text-soil-600 group-hover/heart:text-murram-500'
                        }`}/>
                </button>

                {/* Featured Badge (if applicable) */}
                {property.featured && (
                    <div className="absolute bottom-3 right-3">
                        <div
                            className="bg-jacaranda-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center">
                            <Star className="w-3 h-3 mr-1 fill-current"/>
                            Featured
                        </div>
                    </div>
                )}
            </div>

            {/* Content */}
            <div className="p-5">
                <h3 className="text-lg font-display font-semibold text-soil-900 mb-1.5 group-hover:text-highland-500 transition-colors duration-200 leading-tight">
                    {truncateText(property.title || 'Beautiful Property', 45)}
                </h3>

                <p className="text-soil-600 mb-3 flex items-center text-sm">
                    <MapPin className="w-4 h-4 mr-1.5 text-murram-500 flex-shrink-0"/>
                    <span className="truncate">
                        {truncateText(property.location || property.address || 'Nairobi, Kenya', 35)}
                    </span>
                </p>

                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4 text-sm text-soil-600">
                        {property.bedrooms && (
                            <div className="flex items-center">
                                <Bed className="w-4 h-4 mr-1 flex-shrink-0"/>
                                <span>{property.bedrooms}</span>
                            </div>
                        )}
                        {property.bathrooms && (
                            <div className="flex items-center">
                                <Bath className="w-4 h-4 mr-1 flex-shrink-0"/>
                                <span>{property.bathrooms}</span>
                            </div>
                        )}
                        {property.area && (
                            <div className="flex items-center">
                                <Square className="w-4 h-4 mr-1 flex-shrink-0"/>
                                <span className="text-xs">{property.area}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-soil-100">
                    <span className="font-mono text-xl font-bold text-highland-500 block">
                        {formatPrice(property.price)}
                    </span>
                </div>

                {property.amenities && property.amenities.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-soil-100">
                        <div className="flex flex-wrap gap-2">
                            {property.amenities.slice(0, 3).map((amenity, index) => (
                                <span
                                    key={index}
                                    className="px-2 py-1 bg-highland-50 text-highland-600 text-xs rounded-full"
                                >
                                    {amenity}
                                </span>
                            ))}
                            {property.amenities.length > 3 && (
                                <span className="px-2 py-1 bg-soil-50 text-soil-600 text-xs rounded-full">
                                    +{property.amenities.length - 3} more
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PropertyCard;
